import { useMutation } from '@tanstack/react-query'
import { ObjectsFactory, Model } from '../core'
import type { IHttpError, IResponse } from '../core'
import { AbilityBuilder, PureAbility, AbilityClass } from '@casl/ability'
import { RoleIdEnum } from './role'

// CASL Types
export type Actions = 'create' | 'read' | 'update' | 'delete' | 'manage' | 'approve' | 'view'
export type Subjects =
  | 'employee_list'
  | 'lift_list'
  | 'employer_list'
  | 'attendance_list'
  | 'balance_list'
  | 'transaction_list'
  | 'voucher_list'
  // | 'User'
  // | 'Role'
  // | 'Permission'
  // | 'Game'
  // | 'Transaction'
  // | 'Commission'
  // | 'Point'
  // | 'Branch'
  | 'all'

export type AppAbility = PureAbility<[Actions, Subjects]>
export const AppAbility = PureAbility as AbilityClass<AppAbility>

export interface IPermissions {
  id: number
  created_at: string
  updated_at: string
  resource: string
  description: string
  path: string
  method: string
  name: string
  is_default: boolean
  role: IPermissionsRole[]
}

export interface IPermissionsRole {
  id: number
  created_at: string
  updated_at: string
  name: string
  code: string
  description: string
}

export interface IPermissionsPayload {
  name: string
  description: string
  permissions: number[]
}

const modelConfig = {
  path: '/permissions'
}

export class Permissions extends Model {
  static queryKeys = {
    paginate: 'PERMISSION_PAGINATE_QUERY',
    findOne: 'PERMISSION_FIND_ONE_QUERY'
  }

  static objects = ObjectsFactory.factory<IPermissions>(modelConfig, this.queryKeys)

  static updatePermissionsById(id: number, payload: IPermissionsPayload) {
    return this.api.put<IResponse<IPermissions>>({
      url: `/roles/${id}`,
      data: payload
    })
  }
}

Permissions.setup(modelConfig)

export function useUpdatePermissionByIdMutation() {
  return useMutation<IResponse<IPermissions>, IHttpError, { id: number; payload: IPermissionsPayload }>({
    mutationFn: ({ id, payload }) => {
      return Permissions.updatePermissionsById(id, payload).then(res => res.data)
    }
  })
}

export function defineAbilitiesFor(user: any): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(AppAbility)

  const menu: Record<string, boolean> = {
    user_management: false,
    employer_management: false,
    attendance_management: false,
    finance_management: false
  }

  if (!user) {
    return build()
  }

  // Role-based permissions từ database
  if (user.role?.id === RoleIdEnum.SUPER_ADMIN) {
    can('manage', 'all')
    menu.user_management = true
    menu.employer_management = true
    menu.attendance_management = true
    menu.finance_management = true
  } else if (user.role?.permission) {
    user.role.permission.forEach((permission: any) => {
      const action = permission.method.toLowerCase() as Actions
      const subject = permission.name as Subjects

      menu[`${permission.resource}_management`] = true
      if (user.role?.id === RoleIdEnum.USER_EMPLOYER && subject === 'balance_list') {
        can(action, subject)
      } else
        // can(action, subject)
        can('manage', subject)
    })
  }

  // for menu level 1
  Object.entries(menu).forEach(([key, value]) => {
    if (value) {
      can('manage', key as Subjects)
    }
  })

  can('read', 'all')

  return build()
}
